/**
 * Trip and route management storage service
 *
 * Handles trip planning, route management, waypoints, and track data with
 * Turf.js integration for geospatial operations and offline-first patterns.
 */

import { db } from '../database/schema.js'
import type { Trip, Track, Waypoint } from '../database/schema.js'
import {
  OfflineFirstStorageService,
  type StorageResult,
  type QueryOptions,
} from './base-storage.js'
import { validateTrip, validateTrack, validateWaypoint, runValidation } from './validation.js'
import {
  isValidPosition,
  formatDistance,
  formatElevation,
  type UserPreferences,
} from '../lib/coordinates.js'
import {
  bboxPolygon as turfBboxPolygon,
  area as turfArea,
  point,
  distance as turfDistance,
  centroid,
  bbox as turfBbox,
  points,
  simplify,
  geomEach,
  coordEach,
  length as turfLength,
  lineString,
} from '@turf/turf'

/**
 * Trip creation data
 */
export interface CreateTripData {
  name: string
  description: string
  location: string
  difficulty: Trip['difficulty']
  dateRange: {
    startDate: string
    endDate: string
  }
  teamLeader: string
  createdBy: string
  status: Trip['status']
  bounds?:
    | {
        ne: [number, number]
        sw: [number, number]
      }
    | undefined
}

/**
 * Track creation data
 */
export interface CreateTrackData {
  tripId: string
  name: string
  description?: string | undefined
  type: Track['type']
  geojson: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry // GeoJSON data
  startTime?: string | undefined // ISO timestamp of first track point
  endTime?: string | undefined // ISO timestamp of last track point
  duration?: number | undefined // Duration in seconds
}

/**
 * Waypoint creation data
 */
export interface CreateWaypointData {
  tripId: string
  name: string
  description?: string | undefined
  type: Waypoint['type']
  coordinates: [number, number]
  elevation?: number | undefined
}

/**
 * Trip with calculated metrics
 */
export interface TripWithMetrics extends Trip {
  memberCount?: number | undefined
  trackCount?: number | undefined
  waypointCount?: number | undefined
  totalDistance?: number | undefined
  totalElevationGain?: number | undefined
  estimatedDuration?: number | undefined
  area?: number | undefined
}

/**
 * Track with calculated metrics
 */
export interface TrackWithMetrics extends Track {
  formattedDistance?: string | undefined
  formattedElevation?: string | undefined
  center?: [number, number] | undefined
}

/**
 * Trip search filters
 */
export interface TripSearchFilters {
  status?: Trip['status'][] | undefined
  difficulty?: Trip['difficulty'][] | undefined
  dateRange?:
    | {
        startDate?: string | undefined
        endDate?: string | undefined
      }
    | undefined
  location?: string | undefined
  createdBy?: string | undefined
  teamLeader?: string | undefined
}

/**
 * Trip storage service with geospatial operations
 */
export class TripStorageService extends OfflineFirstStorageService<Trip> {
  constructor() {
    super(db.trips, 'trip')
  }

  /**
   * Create a new trip with validation
   */
  async createTrip(tripData: CreateTripData): Promise<StorageResult<Trip>> {
    const validation = runValidation(validateTrip, tripData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.create(tripData)
  }

  /**
   * Update trip with validation
   */
  async updateTrip(id: string, updates: Partial<Trip>): Promise<StorageResult<Trip>> {
    const validation = runValidation(validateTrip, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.update(id, updates)
  }

  /**
   * Get trip with calculated metrics
   */
  async getTripWithMetrics(tripId: string): Promise<StorageResult<TripWithMetrics | undefined>> {
    return this.executeOperation(async () => {
      const trip = await this.table.get(tripId)
      if (!trip) {
        return undefined
      }

      // Get related data
      const [members, tracks, waypoints] = await Promise.all([
        db.tripMembers.where('tripId').equals(tripId).toArray(),
        db.tracks.where('tripId').equals(tripId).toArray(),
        db.waypoints.where('tripId').equals(tripId).toArray(),
      ])

      // Calculate metrics
      const totalDistance = tracks.reduce((sum, track) => sum + (track.distance || 0), 0)
      const totalElevationGain = tracks.reduce((sum, track) => {
        return sum + (track.elevation?.gain ?? 0)
      }, 0)

      // Calculate trip area if bounds exist
      let tripArea: number | undefined
      if (trip.bounds) {
        const bboxArray = [
          trip.bounds.sw[1], // west
          trip.bounds.sw[0], // south
          trip.bounds.ne[1], // east
          trip.bounds.ne[0], // north
        ] as [number, number, number, number]

        const bboxPoly = turfBboxPolygon(bboxArray)
        tripArea = turfArea(bboxPoly)
      }

      const tripWithMetrics: TripWithMetrics = {
        ...trip,
        memberCount: members.length,
        trackCount: tracks.length,
        waypointCount: waypoints.length,
        totalDistance,
        totalElevationGain,
        area: tripArea,
      }

      return tripWithMetrics
    }, 'get trip with metrics')
  }

  /**
   * Get trips for a user with filters
   */
  async getTripsForUser(
    userId: string,
    filters: TripSearchFilters = {},
    options: QueryOptions = {}
  ): Promise<StorageResult<Trip[]>> {
    return this.executeOperation(async () => {
      // Get user's trip memberships
      const memberships = await db.tripMembers.where('userId').equals(userId).toArray()

      const tripIds = memberships.map(m => m.tripId)

      if (tripIds.length === 0) {
        return []
      }

      // Get trips and apply filters
      let trips = await this.table.where('id').anyOf(tripIds).toArray()

      // Apply filters
      if (filters.status && filters.status.length > 0) {
        trips = trips.filter(trip => filters.status!.includes(trip.status))
      }

      if (filters.difficulty && filters.difficulty.length > 0) {
        trips = trips.filter(trip => filters.difficulty!.includes(trip.difficulty))
      }

      if (filters.location) {
        const locationQuery = filters.location.toLowerCase()
        trips = trips.filter(trip => trip.location.toLowerCase().includes(locationQuery))
      }

      if (filters.dateRange) {
        if (filters.dateRange.startDate) {
          const startDate = new Date(filters.dateRange.startDate)
          trips = trips.filter(trip => new Date(trip.dateRange.endDate) >= startDate)
        }
        if (filters.dateRange.endDate) {
          const endDate = new Date(filters.dateRange.endDate)
          trips = trips.filter(trip => new Date(trip.dateRange.startDate) <= endDate)
        }
      }

      // Apply sorting
      if (options.orderBy) {
        trips.sort((a, b) => {
          const aVal = a[options.orderBy! as keyof Trip]
          const bVal = b[options.orderBy! as keyof Trip]
          const result = aVal! > bVal! ? 1 : aVal! < bVal! ? -1 : 0
          return options.orderDirection === 'desc' ? -result : result
        })
      }

      // Apply pagination
      if (options.offset) {
        trips = trips.slice(options.offset)
      }
      if (options.limit) {
        trips = trips.slice(0, options.limit)
      }

      return trips
    }, 'get trips for user')
  }

  /**
   * Search trips by location with geospatial operations
   */
  async searchTripsByLocation(
    center: [number, number],
    radiusKm: number,
    options: QueryOptions = {}
  ): Promise<StorageResult<Trip[]>> {
    return this.executeOperation(async () => {
      const centerPoint = point([center[1], center[0]]) // [lng, lat] for Turf.js

      const allTrips = await this.table.toArray()

      const nearbyTrips = allTrips.filter(trip => {
        if (!trip.bounds) {
          return false
        }

        const bboxArray = [
          trip.bounds.sw[1], // west
          trip.bounds.sw[0], // south
          trip.bounds.ne[1], // east
          trip.bounds.ne[0], // north
        ] as [number, number, number, number]

        const bboxPoly = turfBboxPolygon(bboxArray)
        const dist = turfDistance(centerPoint, centroid(bboxPoly), {
          units: 'kilometers',
        })

        return dist <= radiusKm
      })

      // Apply limit if specified
      if (options.limit) {
        return nearbyTrips.slice(0, options.limit)
      }

      return nearbyTrips
    }, 'search trips by location')
  }

  /**
   * Get trips with overlapping date ranges
   */
  async getConflictingTrips(
    userId: string,
    startDate: string,
    endDate: string
  ): Promise<StorageResult<Trip[]>> {
    return this.executeOperation(async () => {
      const userTrips = await this.getTripsForUser(userId)
      if (!userTrips.success || !userTrips.data) {
        return []
      }

      const newStart = new Date(startDate)
      const newEnd = new Date(endDate)

      const conflictingTrips = userTrips.data.filter(trip => {
        const tripStart = new Date(trip.dateRange.startDate)
        const tripEnd = new Date(trip.dateRange.endDate)

        // Check for overlap
        return !(newEnd < tripStart || newStart > tripEnd)
      })

      return conflictingTrips
    }, 'get conflicting trips')
  }

  /**
   * Update trip status
   */
  async updateTripStatus(tripId: string, status: Trip['status']): Promise<StorageResult<Trip>> {
    return this.update(tripId, { status })
  }

  /**
   * Calculate trip bounds from tracks and waypoints
   */
  async calculateTripBounds(
    tripId: string
  ): Promise<StorageResult<{ ne: [number, number]; sw: [number, number] }>> {
    return this.executeOperation(async () => {
      const [tracks, waypoints] = await Promise.all([
        db.tracks.where('tripId').equals(tripId).toArray(),
        db.waypoints.where('tripId').equals(tripId).toArray(),
      ])

      const allCoordinates: number[][] = []

      // Extract coordinates from tracks
      tracks.forEach(track => {
        allCoordinates.push([track.bounds.ne[1], track.bounds.ne[0]]) // [lng, lat]
        allCoordinates.push([track.bounds.sw[1], track.bounds.sw[0]]) // [lng, lat]
      })

      // Extract coordinates from waypoints
      waypoints.forEach(waypoint => {
        allCoordinates.push([waypoint.coordinates[1], waypoint.coordinates[0]]) // [lng, lat]
      })

      if (allCoordinates.length === 0) {
        throw new Error('No coordinates found for trip')
      }

      const bounds = turfBbox(points(allCoordinates))

      return {
        sw: [bounds[1], bounds[0]], // [lat, lng]
        ne: [bounds[3], bounds[2]], // [lat, lng]
      }
    }, 'calculate trip bounds')
  }
}

/**
 * Track storage service with geospatial operations
 */
export class TrackStorageService extends OfflineFirstStorageService<Track> {
  constructor() {
    super(db.tracks, 'track')
  }

  /**
   * Create a new track with validation and metrics calculation
   */
  async createTrack(trackData: CreateTrackData): Promise<StorageResult<Track>> {
    const validation = runValidation(validateTrack, trackData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.executeOperation(async () => {
      const id = this.generateId()
      const now = new Date().toISOString()

      // Calculate track metrics from GeoJSON
      const metrics = this.calculateTrackMetrics(trackData.geojson)

      const track: Track = {
        id,
        tripId: trackData.tripId,
        name: trackData.name,
        description: trackData.description,
        type: trackData.type,
        geojson: trackData.geojson,
        bounds: metrics.bounds,
        pointCount: metrics.pointCount,
        distance: metrics.distance,
        elevation: metrics.elevation,
        startTime: trackData.startTime,
        endTime: trackData.endTime,
        duration: trackData.duration,
        createdAt: now,
        updatedAt: now,
      }

      await this.table.add(track)
      return track
    }, 'create track')
  }

  /**
   * Get track with formatted metrics
   */
  async getTrackWithMetrics(
    trackId: string,
    preferences?: UserPreferences
  ): Promise<StorageResult<TrackWithMetrics | undefined>> {
    return this.executeOperation(async () => {
      const track = await this.table.get(trackId)
      if (!track) {
        return undefined
      }

      const trackWithMetrics: TrackWithMetrics = { ...track }

      if (preferences) {
        if (track.distance) {
          trackWithMetrics.formattedDistance = formatDistance(track.distance, preferences)
        }
        if (track.elevation?.gain) {
          trackWithMetrics.formattedElevation = formatElevation(track.elevation.gain, preferences)
        }
      }

      // Calculate center point
      const centerLat = (track.bounds.ne[0] + track.bounds.sw[0]) / 2
      const centerLng = (track.bounds.ne[1] + track.bounds.sw[1]) / 2
      trackWithMetrics.center = [centerLat, centerLng]

      return trackWithMetrics
    }, 'get track with metrics')
  }

  /**
   * Get tracks for a trip
   */
  async getTracksForTrip(tripId: string): Promise<StorageResult<Track[]>> {
    return this.executeOperation(
      () => this.table.where('tripId').equals(tripId).toArray(),
      'get tracks for trip'
    )
  }

  /**
   * Simplify track for better performance
   */
  async simplifyTrack(trackId: string, tolerance: number = 0.0001): Promise<StorageResult<Track>> {
    return this.executeOperation(async () => {
      const track = await this.table.get(trackId)
      if (!track) {
        throw new Error('Track not found')
      }

      const geojson = typeof track.geojson === 'string' ? JSON.parse(track.geojson) : track.geojson
      const simplified = simplify(geojson, { tolerance, highQuality: true })

      // Recalculate metrics for simplified track
      const metrics = this.calculateTrackMetrics(simplified)

      const updatedTrack = {
        ...track,
        geojson: simplified,
        bounds: metrics.bounds,
        pointCount: metrics.pointCount,
        distance: metrics.distance,
        elevation: metrics.elevation,
        updatedAt: new Date().toISOString(),
      }

      await this.table.put(updatedTrack)
      return updatedTrack
    }, 'simplify track')
  }

  /**
   * Calculate track metrics from GeoJSON
   */
  private calculateTrackMetrics(
    geojson: GeoJSON.FeatureCollection | GeoJSON.Feature | GeoJSON.Geometry
  ): {
    bounds: { ne: [number, number]; sw: [number, number] }
    pointCount: number
    distance: number
    elevation?:
      | {
          gain: number
          loss: number
          max: number
          min: number
        }
      | undefined
  } {
    const trackBbox = turfBbox(geojson)
    const bounds: { ne: [number, number]; sw: [number, number] } = {
      sw: [trackBbox[1], trackBbox[0]], // [lat, lng]
      ne: [trackBbox[3], trackBbox[2]], // [lat, lng]
    }

    // Count points
    let pointCount = 0
    geomEach(geojson, (geometry: GeoJSON.Geometry) => {
      if (geometry.type === 'Point') {
        pointCount++
      } else if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
        coordEach(geometry, () => pointCount++)
      }
    })

    let totalDist = 0
    geomEach(geojson, (geometry: GeoJSON.Geometry) => {
      if (geometry.type === 'LineString') {
        totalDist += turfLength(lineString(geometry.coordinates), { units: 'meters' })
      } else if (geometry.type === 'MultiLineString') {
        geometry.coordinates.forEach((line: number[][]) => {
          totalDist += turfLength(lineString(line), { units: 'meters' })
        })
      }
    })

    // Calculate elevation metrics if available
    let elevation: { gain: number; loss: number; max: number; min: number } | undefined
    const elevations: number[] = []

    coordEach(geojson, (coord: number[]) => {
      if (coord.length > 2 && typeof coord[2] === 'number') {
        elevations.push(coord[2])
      }
    })

    if (elevations.length > 0) {
      let gain = 0
      let loss = 0
      const max = Math.max(...elevations)
      const min = Math.min(...elevations)

      for (let i = 1; i < elevations.length; i++) {
        const diff = elevations[i] - elevations[i - 1]
        if (diff > 0) {
          gain += diff
        } else {
          loss += Math.abs(diff)
        }
      }

      elevation = { gain, loss, max, min }
    }

    return {
      bounds,
      pointCount,
      distance: totalDist,
      elevation,
    }
  }
}

/**
 * Waypoint storage service
 */
export class WaypointStorageService extends OfflineFirstStorageService<Waypoint> {
  constructor() {
    super(db.waypoints, 'waypoint')
  }

  /**
   * Create a new waypoint with validation
   */
  async createWaypoint(waypointData: CreateWaypointData): Promise<StorageResult<Waypoint>> {
    const validation = runValidation(validateWaypoint, waypointData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    return this.create(waypointData)
  }

  /**
   * Get waypoints for a trip
   */
  async getWaypointsForTrip(tripId: string): Promise<StorageResult<Waypoint[]>> {
    return this.executeOperation(
      () => this.table.where('tripId').equals(tripId).toArray(),
      'get waypoints for trip'
    )
  }

  /**
   * Get waypoints by type
   */
  async getWaypointsByType(
    tripId: string,
    type: Waypoint['type']
  ): Promise<StorageResult<Waypoint[]>> {
    return this.executeOperation(async () => {
      return this.table
        .where('tripId')
        .equals(tripId)
        .and(waypoint => waypoint.type === type)
        .toArray()
    }, 'get waypoints by type')
  }

  /**
   * Find waypoints near a location
   */
  async getWaypointsNearLocation(
    center: [number, number],
    radiusKm: number
  ): Promise<StorageResult<Waypoint[]>> {
    return this.executeOperation(async () => {
      const centerPoint = point([center[1], center[0]]) // [lng, lat] for Turf.js

      const allWaypoints = await this.table.toArray()

      const nearbyWaypoints = allWaypoints.filter(waypoint => {
        const waypointPoint = point([waypoint.coordinates[1], waypoint.coordinates[0]])
        const dist = turfDistance(centerPoint, waypointPoint, { units: 'kilometers' })
        return dist <= radiusKm
      })

      return nearbyWaypoints
    }, 'get waypoints near location')
  }

  /**
   * Update waypoint coordinates with validation
   */
  async updateWaypointCoordinates(
    waypointId: string,
    coordinates: [number, number],
    elevation?: number
  ): Promise<StorageResult<Waypoint>> {
    // Validate coordinates
    if (!isValidPosition({ lat: coordinates[0], lng: coordinates[1] })) {
      return {
        success: false,
        error: this.createError(
          'INVALID_COORDINATES',
          'Invalid coordinate values',
          'validation',
          coordinates
        ),
      }
    }

    const updates: Partial<Waypoint> = { coordinates }
    if (elevation !== undefined) {
      updates.elevation = elevation
    }

    return this.update(waypointId, updates)
  }
}

// Create and export singleton instances
export const tripStorage = new TripStorageService()
export const trackStorage = new TrackStorageService()
export const waypointStorage = new WaypointStorageService()
